import { ExclamationmarkIcon, Tooltip } from '@bp/ui-components';
import dayjs from 'dayjs';
import React, { ReactNode } from 'react';
import { isPlaced } from '../../../stores/TimetableVersion/SubjectContainerTypes';
import { getConflictingCardLabel } from '../../../utils/getConflictingCardLabel';
import { getWarningTypeTranslation } from '../../../utils/getWarningTypeTranslation';
import { useTimetableStore } from '../TimetableProvider';
import styles from './CardInfo.module.scss';

type Props = {
  mode?: 'icon' | 'list';
  onToggle?: () => void;
  active?: boolean;
};

type ConflictItem = {
  content: ReactNode;
  conflictingCardLabel?: string;
};

export const CardInfoConflict = ({ mode = 'icon', onToggle, active }: Props) => {
  const store = useTimetableStore();

  const selectedCard = store.pinnedCard || store.hoveredCard ? store.getCardToShowOnInfo() : null;

  const cardConflicts = selectedCard ? store.cardConflicts.get(selectedCard.uuid) : null;

  const conflicts: ConflictItem[] = [];
  const maybe: ConflictItem[] = [];

  const getEpochDateRanges = (resourceUuid: string, type: 'teacher' | 'room', placedUuids: string[]): string[] => {
    const fmt = (d: string) => dayjs(d).format('DD.MM.');
    const ranges: string[] = [];
    placedUuids.forEach((uid) => {
      store.subjectContainerSlots
        .get(uid)
        ?.filter(isPlaced)
        .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
        .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
    });
    if (ranges.length === 0 && selectedCard) {
      store.subjectContainerSlots
        .get(selectedCard.uuid)
        ?.filter(isPlaced)
        .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
        .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
    }
    return ranges;
  };

  cardConflicts?.forEach((conflict, key) => {
    const placedWithoutCurrent = Array.from(conflict.placed).filter((uuid) => uuid !== selectedCard?.uuid);

    if (conflict.type === 'class' && conflict.maybe) {
      maybe.push({
        content: (
          <>
            {getWarningTypeTranslation('classMaybe')} ({store.getNameForUuid(key, 'class')})
          </>
        ),
      });
    }
    if (conflict.type === 'teacher' && conflict.maybe) {
      maybe.push({
        content: (
          <>
            {getWarningTypeTranslation('teacherMaybe')} ({store.getNameForUuid(key, 'teacher')})
          </>
        ),
      });
    }
    if (conflict.type === 'room' && conflict.maybe) {
      maybe.push({
        content: (
          <>
            {getWarningTypeTranslation('roomMaybe')} ({store.getNameForUuid(key, 'room')})
          </>
        ),
      });
    }
    if (conflict.type === 'subject' && conflict.maybe) {
      maybe.push({
        content: (
          <>
            {getWarningTypeTranslation('subjectMaybe')} ({store.getNameForUuid(key, 'subject')})
          </>
        ),
      });
    }
    if (conflict.type === 'subject' && conflict.blocked) {
      conflicts.push({ content: <>{getWarningTypeTranslation('subjectNotAvailable')}</> });
    }
    if (conflict.type === 'class' && conflict.blocked) {
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('classNotAvailable')} ({store.getNameForUuid(key, 'class')})
          </>
        ),
      });
    }
    if (conflict.type === 'class' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('overlappingClass')} ({store.getNameForUuid(key, 'class')})
          </>
        ),
        conflictingCardLabel: getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid)),
      });
    }
    if (conflict.type === 'teacher' && conflict.blocked) {
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('teacherNotAvailable')} ({store.getNameForUuid(key, 'teacher')})
          </>
        ),
      });
    }
    if (conflict.type === 'teacher' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
      const epochRanges = getEpochDateRanges(key, 'teacher', placedWithoutCurrent);
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('overlappingTeachers')} ({store.getNameForUuid(key, 'teacher')})
            {epochRanges.length > 0 && <span className={styles['epoch-range']}>{epochRanges.join(', ')}</span>}
          </>
        ),
        conflictingCardLabel: getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid)),
      });
    }
    if (conflict.type === 'room' && conflict.blocked) {
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('roomNotAvailable')} ({store.getNameForUuid(key, 'room')})
          </>
        ),
      });
    }
    if (conflict.type === 'room' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
      const epochRanges = getEpochDateRanges(key, 'room', placedWithoutCurrent);
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('overlappingRooms')} ({store.getNameForUuid(key, 'room')})
            {epochRanges.length > 0 && <span className={styles['epoch-range']}>{epochRanges.join(', ')}</span>}
          </>
        ),
        conflictingCardLabel: getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid)),
      });
    }
    if (conflict.type === 'divisionGroup' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
      conflicts.push({
        content: (
          <>
            {getWarningTypeTranslation('notMatchingDivision')} ({store.getNameForUuid(key, 'divisionGroup')})
          </>
        ),
        conflictingCardLabel: getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid)),
      });
    }
  });

  const allItems = [...conflicts, ...maybe];

  if (mode === 'list') {
    return (
      <div className={styles['conflict-list']}>
        {allItems.map((item, i) => (
          <div key={i} className={styles['conflict-list-item']}>
            <span>{item.content}</span>
            {item.conflictingCardLabel && (
              <span className={styles['conflict-with-card']}>{item.conflictingCardLabel}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {selectedCard && conflicts.length > 0 && (
        <Tooltip
          triggerClass={active ? styles['warning-active'] : styles.warning}
          content={allItems.map((item, i) => {
            return <div key={i}>{item.content}</div>;
          })}
        >
          <button className={styles['conflict-icon-btn']} onClick={onToggle}>
            <ExclamationmarkIcon />
          </button>
        </Tooltip>
      )}
    </>
  );
};
